import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { getDeliveryOptions } from '../utils/deliveryDate';
import { useDesign } from '../contexts/DesignContext';
import ProductMediaWithSwatches from '../components/ProductMediaWithSwatches';
import ProductGridSkeleton from '../components/ProductGridSkeleton';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryImageErrors, setCategoryImageErrors] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsHeroImageIndex, setProductsHeroImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatPrice } = useCurrency();
  const { designConfig } = useDesign();
  const { isAuthenticated } = useAuth();
  const { wishlistProductIds, toggleWishlist } = useWishlist();

  const handleToggleWishlist = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please login to save items to your wishlist');
      return;
    }
    const wasInWishlist = wishlistProductIds.has(productId);
    const result = await toggleWishlist(productId);
    if (result.success) {
      toast.success(wasInWishlist ? 'Removed from wishlist' : 'Added to wishlist');
    } else {
      toast.error(result.message || 'Failed to update wishlist');
    }
  };

  const getAssetUrl = (assetPath) => {
    if (!assetPath) return '';
    if (/^https?:\/\//i.test(assetPath)) return assetPath;

    const API_BASE = process.env.REACT_APP_API_URL || '';
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    return `${API_BASE}${normalizedPath}`;
  };
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    subcategory: searchParams.get('subcategory') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sortBy: searchParams.get('sortBy') || 'name',
    sortOrder: searchParams.get('sortOrder') || 'asc'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  // Sync filters with URL params on mount and when URL changes
  useEffect(() => {
    const urlSearch = (searchParams.get('search') || '').trim().toLowerCase();
    const explicitCategory = searchParams.get('category') || '';
    const bagSearchFallbackCategory = (!explicitCategory && (urlSearch === 'bag' || urlSearch === 'bags'))
      ? '21'
      : '';

    setFilters({
      search: searchParams.get('search') || '',
      category: explicitCategory || bagSearchFallbackCategory,
      subcategory: searchParams.get('subcategory') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      sortBy: searchParams.get('sortBy') || 'name',
      sortOrder: searchParams.get('sortOrder') || 'asc'
    });
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Get all subcategories recursively from a category
  const getAllSubcategories = useCallback((category) => {
    let allSubs = [];
    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach(sub => {
        allSubs.push(sub);
        // Recursively get nested subcategories
        if (sub.subcategories) {
          allSubs = allSubs.concat(getAllSubcategories(sub));
        }
      });
    }
    return allSubs;
  }, []);

  // Handle category selection
  const handleCategoryChange = useCallback((categoryId) => {
    const category = categories.find(cat => cat.id.toString() === categoryId.toString());
    if (category) {
      setSelectedCategory(category);
      const subs = getAllSubcategories(category);
      setSubcategories(subs);
    } else {
      setSelectedCategory(null);
      setSubcategories([]);
    }
  }, [categories, getAllSubcategories]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = `${API_BASE}/api/products?${queryParams}`;
      
      console.log('🛍️ [Products] Fetching products from:', url);
      
      const response = await fetch(url);
      
      console.log('🛍️ [Products] Response status:', response.status);
      
      if (!response.ok) {
        console.error('❌ [Products] Response not OK:', response.status, response.statusText);
        setProducts([]);
        return;
      }
      
      const data = await response.json();
      console.log('🛍️ [Products] Response data:', {
        success: data.success,
        hasProducts: !!data.products,
        productsCount: data.products?.length || 0,
        totalProducts: data.totalProducts
      });
      
      if (data.success) {
        // Handle both response formats: data.products or data.data
        const productsList = data.products || data.data || [];
        console.log('✅ [Products] Setting products:', productsList.length);
        setProducts(productsList);
      } else {
        console.error('❌ [Products] API returned success: false:', data.message);
        setProducts([]);
      }
    } catch (error) {
      console.error('❌ [Products] Error fetching products:', error);
      console.error('❌ [Products] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Update subcategories when category filter changes
  useEffect(() => {
    if (filters.category && categories.length > 0) {
      handleCategoryChange(filters.category);
    } else {
      setSelectedCategory(null);
      setSubcategories([]);
    }
  }, [filters.category, categories, handleCategoryChange]);


  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    
    // If category changes, reset subcategory and update subcategories list
    if (key === 'category') {
      newFilters.subcategory = '';
      handleCategoryChange(value);
    }
    
    // If subcategory is cleared, keep category
    if (key === 'subcategory' && !value) {
      // Keep the category filter
    }
    
    setFilters(newFilters);
    
    // Update URL search params
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) newSearchParams.append(k, v);
    });
    setSearchParams(newSearchParams);
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      category: '',
      subcategory: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'name',
      sortOrder: 'asc'
    };
    setFilters(clearedFilters);
    setSelectedCategory(null);
    setSubcategories([]);
    setSearchParams({});
  };

  const productsHeroEnabled = String(designConfig.products_hero_enabled ?? 'true') === 'true';
  const productsHeroUseImage = String(designConfig.products_hero_use_image ?? 'false') === 'true';
  const productsHeroOverlayEnabled = String(designConfig.products_hero_overlay_enabled ?? 'true') === 'true';
  const productsHeroAutoplay = String(designConfig.products_hero_autoplay ?? 'true') === 'true';
  const productsHeroSwipeEnabled = String(designConfig.products_hero_swipe_enabled ?? 'true') === 'true';
  const productsHeroDirection = String(designConfig.products_hero_scroll_direction ?? 'rtl') === 'ltr' ? 'ltr' : 'rtl';
  const parsedAutoplaySpeed = parseInt(designConfig.products_hero_autoplay_speed, 10);
  const productsHeroAutoplaySpeed = Number.isFinite(parsedAutoplaySpeed) && parsedAutoplaySpeed >= 1500
    ? parsedAutoplaySpeed
    : 4000;
  const productsHeroImages = useMemo(() => {
    const raw = (designConfig.products_hero_background_image || '').trim();
    if (!raw) return [];
    return raw
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url)
      .map((url) => getAssetUrl(url));
  }, [designConfig.products_hero_background_image]);
  const productsHeroTopLabel = (designConfig.products_hero_top_label || 'Collection').trim();
  const productsHeroTitle = (designConfig.products_hero_title || 'Our Collection').trim();
  const productsHeroSubtitle = (designConfig.products_hero_subtitle || 'Discover our curated selection of elegant fashion, designer bags, and luxury accessories').trim();

  // When a category with a banner is selected, override hero values
  const activeCategoryBannerImage = selectedCategory?.cover_banner_image
    ? getAssetUrl(selectedCategory.cover_banner_image)
    : null;
  const activeCategoryTitle = selectedCategory?.banner_title
    || (selectedCategory ? `${selectedCategory.name} Collection` : null);
  const activeCategorySubtitle = selectedCategory?.banner_subtitle || null;

  // Effective hero values (category overrides global)
  const effectiveHeroTitle = activeCategoryTitle || productsHeroTitle;
  const effectiveHeroSubtitle = activeCategorySubtitle || productsHeroSubtitle;
  const effectiveHeroImages = activeCategoryBannerImage
    ? [activeCategoryBannerImage]
    : productsHeroImages;
  const effectiveHeroUseImage = activeCategoryBannerImage
    ? true
    : productsHeroUseImage;

  const goToNextHeroImage = useCallback(() => {
    if (effectiveHeroImages.length <= 1) return;
    setProductsHeroImageIndex((prev) => (prev + 1) % effectiveHeroImages.length);
  }, [effectiveHeroImages.length]);

  const goToPrevHeroImage = useCallback(() => {
    if (effectiveHeroImages.length <= 1) return;
    setProductsHeroImageIndex((prev) => (prev - 1 + effectiveHeroImages.length) % effectiveHeroImages.length);
  }, [effectiveHeroImages.length]);

  useEffect(() => {
    if (!productsHeroAutoplay || effectiveHeroImages.length <= 1) {
      setProductsHeroImageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      if (productsHeroDirection === 'rtl') {
        goToNextHeroImage();
      } else {
        goToPrevHeroImage();
      }
    }, productsHeroAutoplaySpeed);

    return () => clearInterval(interval);
  }, [goToNextHeroImage, goToPrevHeroImage, productsHeroAutoplay, productsHeroAutoplaySpeed, productsHeroDirection, effectiveHeroImages.length]);

  const handleHeroTouchStart = useCallback((event) => {
    if (!productsHeroSwipeEnabled || effectiveHeroImages.length <= 1) return;
    setTouchEndX(null);
    setTouchStartX(event.touches[0].clientX);
  }, [effectiveHeroImages.length, productsHeroSwipeEnabled]);

  const handleHeroTouchMove = useCallback((event) => {
    if (!productsHeroSwipeEnabled || effectiveHeroImages.length <= 1) return;
    setTouchEndX(event.touches[0].clientX);
  }, [effectiveHeroImages.length, productsHeroSwipeEnabled]);

  const handleHeroTouchEnd = useCallback(() => {
    if (!productsHeroSwipeEnabled || effectiveHeroImages.length <= 1 || touchStartX === null || touchEndX === null) return;

    const swipeDelta = touchStartX - touchEndX;
    const minSwipeDistance = 40;

    if (Math.abs(swipeDelta) >= minSwipeDistance) {
      if (swipeDelta > 0) {
        goToNextHeroImage();
      } else {
        goToPrevHeroImage();
      }
    }

    setTouchStartX(null);
    setTouchEndX(null);
  }, [goToNextHeroImage, goToPrevHeroImage, effectiveHeroImages.length, productsHeroSwipeEnabled, touchEndX, touchStartX]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFF8F5' }}>
        {/* Header Section - Elegant Style */}
        {productsHeroEnabled && (
          <div
            className="text-white py-12 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden"
            style={{
              background: effectiveHeroUseImage && effectiveHeroImages.length > 0
                ? '#111827'
                : `linear-gradient(135deg, #D4AF37 0%, #C9A9DD 50%, #F4A5C7 100%)`,
              zIndex: 1,
              position: 'relative'
            }}
          >
            {effectiveHeroUseImage && effectiveHeroImages.length > 0 && (
              <div className="absolute inset-0 overflow-hidden" onTouchStart={handleHeroTouchStart} onTouchMove={handleHeroTouchMove} onTouchEnd={handleHeroTouchEnd} style={{ cursor: productsHeroSwipeEnabled ? 'grab' : 'default' }}>
                <div className="flex h-full transition-transform duration-700 ease-out" style={{ width: `${effectiveHeroImages.length * 100}%`, transform: `translateX(-${productsHeroImageIndex * (100 / effectiveHeroImages.length)}%)` }}>
                  {effectiveHeroImages.map((image, index) => (
                    <div key={`products-hero-slide-${index}`} className="h-full" style={{ width: `${100 / effectiveHeroImages.length}%` }}>
                      <img src={image} alt={`Products hero ${index + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="absolute inset-0" style={{ background: effectiveHeroUseImage && effectiveHeroImages.length > 0 ? 'linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.2) 100%)' : 'transparent', opacity: effectiveHeroUseImage && effectiveHeroImages.length > 0 && !productsHeroOverlayEnabled ? 0 : 1 }} />
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '40px 40px', display: effectiveHeroUseImage && effectiveHeroImages.length > 0 ? 'none' : 'block' }}></div>

            <div className="container mx-auto max-w-full px-3 sm:px-4 md:px-6 text-center relative z-10">
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <div className="h-px w-8 sm:w-12 md:w-16" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}></div>
                <div className="mx-2 sm:mx-4 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white"></div>
                <div className="h-px w-8 sm:w-12 md:w-16" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}></div>
              </div>
              <p className="text-xs sm:text-sm tracking-[0.35em] uppercase text-white/85 mb-3">{productsHeroTopLabel || 'Collection'}</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light mb-4 sm:mb-6 tracking-wide px-4" style={{ fontFamily: 'serif', textShadow: effectiveHeroUseImage && effectiveHeroImages.length > 0 ? '0 2px 10px rgba(0,0,0,0.6)' : '0 4px 20px rgba(0,0,0,0.2)' }}>
                {effectiveHeroTitle}
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 max-w-2xl mx-auto leading-relaxed px-4" style={{ letterSpacing: '0.5px' }}>
                {effectiveHeroSubtitle}
              </p>
              <div className="flex items-center justify-center mt-6 sm:mt-8">
                <div className="h-px w-8 sm:w-12 md:w-16" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}></div>
                <div className="mx-2 sm:mx-4 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white"></div>
                <div className="h-px w-8 sm:w-12 md:w-16" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}></div>
              </div>
              {effectiveHeroImages.length > 1 && (
                <div className="mt-5 flex items-center justify-center gap-2">
                  {effectiveHeroImages.map((_, index) => (
                    <button key={`products-hero-dot-${index}`} onClick={() => setProductsHeroImageIndex(index)} className={`h-2 rounded-full transition-all ${index === productsHeroImageIndex ? 'w-8 bg-white' : 'w-2 bg-white/60'}`} aria-label={`Go to hero image ${index + 1}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subcategory Quick Links - shown when a category with subcategories is selected */}
        {selectedCategory && subcategories.length > 0 && (
          <div className="bg-white border-b border-gray-100 py-4 px-3 sm:px-4 md:px-6">
            <div className="container mx-auto max-w-full">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Browse {selectedCategory.name}</h2>
                <button onClick={() => handleFilterChange('subcategory', '')} className="text-xs text-gray-400 hover:text-gray-600">View All</button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {subcategories.map((sub) => (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => handleFilterChange('subcategory', sub.id)}
                    className={`rounded-xl border px-2 py-2.5 text-center text-xs font-semibold transition-all ${filters.subcategory && filters.subcategory.toString() === sub.id.toString() ? 'border-transparent text-white shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm'}`}
                    style={filters.subcategory && filters.subcategory.toString() === sub.id.toString() ? { backgroundColor: '#C9A9DD' } : {}}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      <div className="container mx-auto max-w-full px-3 sm:px-4 md:px-6 py-6 sm:py-8 md:py-10">
        <div className="mx-auto max-w-full">
          <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-6">
            {/* Left Sidebar Filters (desktop) / Top Filters (mobile) */}
            <aside className="lg:sticky lg:top-24 lg:self-start">
              {/* Filter Section - E-commerce Style */}
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-3 sm:mb-4 border border-gray-200">
                {/* Active Filters */}
                {(filters.category || filters.subcategory || filters.search) && (
                  <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                    {filters.category && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                        {categories.find(c => c.id.toString() === filters.category)?.name || 'Category'}
                        <button
                          onClick={() => handleFilterChange('category', '')}
                          className="ml-1 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.subcategory && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                        {subcategories.find(s => s.id.toString() === filters.subcategory)?.name || 'Subcategory'}
                        <button
                          onClick={() => handleFilterChange('subcategory', '')}
                          className="ml-1 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.search && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                        Search: {filters.search}
                        <button
                          onClick={() => handleFilterChange('search', '')}
                          className="ml-1 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                )}

                {/* Quick Category Strip (with images) */}
                {categories.length > 0 && (
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                      <button
                        type="button"
                        onClick={() => handleFilterChange('category', '')}
                        className={`flex-shrink-0 w-[84px] ${!filters.category ? 'opacity-100' : 'opacity-70 hover:opacity-100'} transition`}
                      >
                        <div className="flex flex-col items-center">
                          <div
                            className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm border border-gray-100 overflow-hidden"
                            style={{ backgroundColor: '#FFFFFF' }}
                          >
                            <span className="text-xs font-bold text-gray-600">ALL</span>
                          </div>
                          <div className="mt-1 text-[11px] text-center leading-tight line-clamp-2 text-gray-700">
                            All
                          </div>
                        </div>
                      </button>

                      {categories.slice(0, 12).map((cat) => {
                        const isSelected = filters.category && cat.id.toString() === filters.category.toString();
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleFilterChange('category', cat.id)}
                            className={`flex-shrink-0 w-[84px] ${isSelected ? 'opacity-100' : 'opacity-70 hover:opacity-100'} transition`}
                          >
                            <div className="flex flex-col items-center">
                              <div
                                className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm border border-gray-100 overflow-hidden"
                                style={{ backgroundColor: '#FFFFFF' }}
                              >
                                {cat.image && !categoryImageErrors[cat.id] ? (
                                  <img
                                    src={getAssetUrl(cat.image)}
                                    alt={cat.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={() => {
                                      setCategoryImageErrors((prev) => ({
                                        ...prev,
                                        [cat.id]: true
                                      }));
                                    }}
                                  />
                                ) : (
                                  <span className="text-lg font-bold text-gray-700">
                                    {(cat.name || '?').trim().charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-[11px] text-center leading-tight line-clamp-2 text-gray-700">
                                {cat.name}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {categories.length > 12 && (
                      <div className="text-xs text-gray-500 px-1">Showing top 12 categories</div>
                    )}
                  </div>
                )}

                {/* Filter Row */}
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end lg:flex-col lg:items-stretch">
                  {/* Price Filter */}
                  <div className="flex flex-col items-start gap-1 w-full sm:w-auto lg:w-full">
                    <span className="text-sm font-medium text-gray-700">Price ({formatPrice(0).replace(/\d+/g, '')})</span>
                    <select 
                      value={filters.maxPrice ? `max-${filters.maxPrice}` : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const maxPrice = e.target.value.replace('max-', '');
                          handleFilterChange('maxPrice', maxPrice);
                        } else {
                          handleFilterChange('maxPrice', '');
                        }
                      }}
                      className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">All Prices</option>
                      <option value="max-100">Under {formatPrice(100)}</option>
                      <option value="max-500">Under {formatPrice(500)}</option>
                      <option value="max-1000">Under {formatPrice(1000)}</option>
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div className="flex flex-col items-start gap-1 w-full sm:w-auto lg:w-full">
                    <span className="text-sm font-medium text-gray-700">Category</span>
                    <select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">All Categories</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subcategory Filter */}
                  {selectedCategory && subcategories.length > 0 && (
                    <div className="flex flex-col items-start gap-1 w-full sm:w-auto lg:w-full">
                      <span className="text-sm font-medium text-gray-700">Subcategory</span>
                      <select
                        value={filters.subcategory}
                        onChange={(e) => handleFilterChange('subcategory', e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">All {selectedCategory.name}</option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Search */}
                  <div className="w-full sm:flex-1 sm:min-w-[200px] lg:w-full">
                    <span className="sr-only">Search</span>
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Search products..."
                      className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Sort */}
                  <div className="flex flex-col items-start gap-1 w-full sm:w-auto lg:w-full">
                    <span className="text-sm font-medium text-gray-700">Sort</span>
                    <select
                      value={`${filters.sortBy}-${filters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split('-');
                        handleFilterChange('sortBy', sortBy);
                        handleFilterChange('sortOrder', sortOrder);
                      }}
                      className="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                      <option value="price-asc">Price: Low to High</option>
                      <option value="price-desc">Price: High to Low</option>
                      <option value="created_at-desc">Newest First</option>
                    </select>
                  </div>

                  <button
                    onClick={clearFilters}
                    className="w-full border border-gray-300 text-gray-700 text-sm font-medium py-2 rounded hover:bg-gray-50 transition"
                    type="button"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </aside>

            <section>
              {/* Products Grid */}
              {loading ? (
                <ProductGridSkeleton count={12} className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4" />
              ) : products.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">No Products Found</h3>
                  <p className="text-gray-500 mb-6">Try adjusting your filters to see more results</p>
                  <button
                    onClick={clearFilters}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300"
                    type="button"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4">
                  {products.map((product, index) => {
              // Parse prices and discount - handle both string and number types
              const originalPrice = (product.original_price !== null && product.original_price !== undefined && product.original_price !== '') 
                ? parseFloat(product.original_price) 
                : null;
              const currentPrice = parseFloat(product.price) || 0;
              const discountAmount = (product.discount !== null && product.discount !== undefined && product.discount !== '' && product.discount !== '0') 
                ? parseFloat(product.discount) 
                : 0;
              
              // Calculate discount percentage
              let discountPercent = 0;
              let hasDiscount = false;
              let showOriginalPrice = false;
              
              // Check if we have original price that's higher than current price
              if (originalPrice && !isNaN(originalPrice) && originalPrice > 0 && originalPrice > currentPrice) {
                discountPercent = Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
                hasDiscount = discountPercent > 0 && discountPercent <= 100;
                showOriginalPrice = true;
              } 
              // If no original price but we have discount amount
              else if (discountAmount && !isNaN(discountAmount) && discountAmount > 0 && currentPrice > 0) {
                const priceWithDiscount = currentPrice + discountAmount;
                discountPercent = Math.round((discountAmount / priceWithDiscount) * 100);
                hasDiscount = discountPercent > 0 && discountPercent <= 100;
                showOriginalPrice = false;
              }
              
              return (
              <div 
                key={product.id} 
                className="group bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-200 border border-gray-200 relative"
              >
                {/* Product Image */}
                <div className="relative overflow-hidden bg-white">
                  <ProductMediaWithSwatches
                    product={product}
                    containerClassName="aspect-square overflow-hidden relative bg-gray-50"
                    imgClassName="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform duration-200"
                  >
                    {/* Out of Stock Badge */}
                    {(!product.stock_quantity || product.stock_quantity <= 0) && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-bold shadow-md z-10">
                        Out of Stock
                      </div>
                    )}
                  </ProductMediaWithSwatches>

                  {/* Badges - Deal */}
                  <div className="absolute top-2 left-2 flex gap-2">
                    {hasDiscount && discountPercent >= 50 && (
                      <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">Deal</span>
                    )}
                  </div>
                  
                  {/* Wishlist Heart Icon - Top Right */}
                  <button
                    onClick={(e) => handleToggleWishlist(e, product.id)}
                    className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-red-50 transition-colors z-10"
                    aria-label={wishlistProductIds.has(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                  >
                    <svg
                      className={`w-5 h-5 transition-colors ${wishlistProductIds.has(product.id) ? 'text-red-500' : 'text-gray-600 hover:text-red-500'}`}
                      fill={wishlistProductIds.has(product.id) ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  
                  {/* Shopping Cart Icon - Bottom Right */}
                  <button className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-green-50 transition-colors opacity-0 group-hover:opacity-100 z-10">
                    <svg className="w-5 h-5 text-gray-600 hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </button>
                </div>

                {/* Product Info */}
                <div className="p-2.5 space-y-2">
                    {/* Product Title */}
                    <Link to={`/products/${product.id}`}>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 hover:text-blue-600 transition-colors min-h-[2rem] sm:min-h-[2.5rem] break-words">
                        {product.name}
                      </h3>
                    </Link>
                  
                  {/* Rating - only shown when the product has real reviews */}
                  {Number(product.review_count) > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-3 h-3 ${
                              i < Math.round(Number(product.avg_rating)) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-xs text-gray-600">
                        {Number(product.avg_rating).toFixed(1)} ({product.review_count})
                      </span>
                    </div>
                  )}

                    {/* Price */}
                    <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {formatPrice(currentPrice)}
                      </span>
                      {showOriginalPrice && originalPrice && (
                        <span className="text-sm text-gray-500 line-through">
                          {formatPrice(originalPrice)}
                        </span>
                      )}
                      {hasDiscount && discountPercent > 0 && (
                        <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded">
                          {discountPercent}% OFF
                        </span>
                      )}
                    </div>
                  
                  {/* Additional Discount Badge */}
                  {hasDiscount && discountPercent >= 50 && (
                    <div className="text-xs text-green-600 font-semibold">
                      Extra 20% off
                    </div>
                  )}
                  
                    {/* Delivery & Shipping Info - the "Free Delivery" line here used to
                        be a hardcoded claim shown regardless of the actual shipping
                        fee/threshold config; removed in favor of the honest delivery
                        estimate that was already being computed correctly below it. */}
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="text-blue-600 font-medium">
                          Delivery expected {getDeliveryOptions(product).express.deliveryText.replace(/^express\s*/i, 'by ')}
                        </span>
                      </div>
                      {/* Show normal delivery date as well */}
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 text-xs">
                          Normal: {getDeliveryOptions(product).normal.formattedDate}
                        </span>
                      </div>
                    </div>
                  
                  {/* Status Badge */}
                  {product.stock_quantity !== undefined && product.stock_quantity < 10 && product.stock_quantity > 0 && (
                    <div className="text-xs text-orange-600 font-semibold">
                      Selling out fast
                    </div>
                  )}
                </div>
              </div>
              );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;